import {useTheme} from "@emotion/react";
import {useNavigation, useSearchParams} from "@remix-run/react";
import type {RefObject} from "react";
import {useEffect, useRef, useState} from "react";
import {AD_TAGS, BASE_PAGE_AD_ID} from "@/components/adSections/index";
import {Slide, Slider} from "@/components/Slider";
import {ResultsAmountWrapper} from "@/components/sections/SearchB16/styles.js";
import CrossContentCard from "@/elements/ContentCard";
import CTA from "@/elements/CTA";
import ArticlePagination from "@/elements/Pagination";
import TitleUnderlinedAnimated from "@/elements/TitleUnderlinedAnimated";
import {Text} from "@/elements/text";
import type {SectionPushContent} from "@/generated/api";
import useHasHydrated from "@/hooks/useHasHydrated";
import {Filters} from "@/layouts/Filters";
import SectionWrapper from "@/layouts/SectionWrapper";
import {theme} from "@/styles/theme";
import {hideAdsWrappers, resizeAdsZoom} from "@/utils/ad";
import {loadAndDisplayAds} from "@/utils/adgw";
import {type ActiveFilter, isShowAll, type ShowAllFilter} from "@/utils/filters";
import {log} from "@/utils/log";
import type {ListedCard, QueriedArticles} from "@/utils/sections/b16";
import type {Translation} from "@/utils/translation";
import {CardsWrapper, InnerWrapper, SliderCardsWrapper, SliderWrapper} from "./styles";

const getShowAllFilter = (translation: Translation | undefined): ShowAllFilter => ({
  name: translation?.showAll || "Show all",
  showAll: true,
});

// The id of a card; the slots of the newsletter card and of an ad have none
const cardId = (card: ListedCard) => ("ID" in card ? card.ID : undefined);

interface B16ArticlesProps {
  amount?: number;
  queriedArticlesResponse?: QueriedArticles;
  sectionRef?: RefObject<HTMLDivElement | null>;
  articleList?: boolean;
  sectionData?: SectionPushContent;
  isFirstSection?: boolean;
  isHomePage?: boolean;
  ad_enabled?: boolean;
  isDestinationPage?: boolean;
  translation?: Translation;
  isLastSection?: boolean;
  hideTitle?: boolean;
  id?: string;
  language?: string;
  pagination?: Array<number | string>;
}

const B16Articles = ({
  amount,
  queriedArticlesResponse,
  sectionRef: ref,
  articleList = false,
  sectionData,
  isFirstSection,
  isHomePage,
  ad_enabled,
  isDestinationPage = false,
  translation,
  isLastSection,
  hideTitle = false,
  id,
  language,
  pagination,
}: B16ArticlesProps) => {
  const navigation = useNavigation();
  const {breakpoints} = useTheme();
  const {isMobile, isDesktop, xl} = breakpoints;
  const hasHydrated = useHasHydrated();

  const [searchParams, setSearchParams] = useSearchParams();
  // use to prevent scroll on first load
  const [isInitialPage, setIsInitialPage] = useState(true);

  const setQueryString = (
    paramsObj: Record<string, string | number | boolean | undefined>,
  ) => {
    Object.entries(paramsObj).forEach(([key, value]) => {
      // An absent value (`params` of a block that has none) is not set: it used to be written as the text "undefined"
      if (value !== undefined) searchParams.set(key, String(value));
    });

    setSearchParams(searchParams);
  };

  const noContentFound = translation?.noContentFound;
  const showAllFilter = getShowAllFilter(translation);

  const currentPage = queriedArticlesResponse?.currentPage ?? 0;
  const localFiltersData = queriedArticlesResponse?.localFiltersData;
  // The "nothing matches" message below is shown when the filter of the list matches no article (the flag is on the response, not on its cards)
  const isEmptyResponse = !!queriedArticlesResponse?.emptyResponse;
  const totalPages = queriedArticlesResponse?.totalPages ?? 0;
  const amountResult = queriedArticlesResponse?.amountResult;
  const internalRef = useRef<HTMLDivElement>(null);
  const sectionRef = articleList ? ref : internalRef;

  const activeFilters: ActiveFilter[] = [
    queriedArticlesResponse?.filterValue
      ? (queriedArticlesResponse.filters ?? showAllFilter)
      : showAllFilter,
  ];

  const amountArticles = activeFilters?.some(isShowAll) ? amount : amountResult;
  const resultTranslation =
    (amountArticles ?? 0) > 1 ? translation?.results : translation?.result;

  const cta = sectionData?.cta;
  const showFilters = sectionData?.display_filters;
  // `filters` is `[]` (a PHP empty array) when no filter is chosen
  const filterTerms = Array.isArray(sectionData?.filters)
    ? undefined
    : sectionData?.filters;
  const moreThanOneFilter = (filterTerms?.post_cat?.length ?? 0) > 1;
  const country = filterTerms?.dest_country?.flatMap((c) =>
    c?.name ? [c.name.toLowerCase()] : [],
  ) ?? ["france"];
  const slider = sectionData?.display_type === "slider";
  const bgColor = sectionData?.bg_color;

  useEffect(() => {
    if (!hasHydrated || typeof window === "undefined") return;

    if (ad_enabled) {
      loadAndDisplayAds(BASE_PAGE_AD_ID, AD_TAGS).catch((error: unknown) => {
        log.error({event: "ads.load_failed", err: error}, "error ad loading");
      });
    }
  }, [hasHydrated, ad_enabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeFilters n'est jamais lu dans le corps, mais sert de déclencheur volontaire (nouvelle référence à chaque changement de filtre) pour relancer le cycle hideAdsWrappers/resizeAdsZoom ; c'est l'unique dépendance, la retirer empêcherait tout redéclenchement lors d'un changement de filtre
  useEffect(() => {
    let callCount = 0;

    const interval = setInterval(() => {
      hideAdsWrappers();
      callCount += 1;

      if (callCount === 6) {
        clearInterval(interval);
      }
      resizeAdsZoom();
    }, 700);

    return () => clearInterval(interval);
  }, [activeFilters]);

  const handleFilter = (filter: ActiveFilter[]) => {
    const isActiveFilters = !filter.some(isShowAll);
    if (!isActiveFilters) {
      setQueryString({});
    }
    if (isActiveFilters || currentPage > 1) {
      // The active filters of a block are the `&fs[taxonomy]=id` fragments of its terms (see layouts/Filters)
      const fragments = filter.filter((item): item is string => typeof item === "string");
      const filterValues = isActiveFilters
        ? fragments.map((item) => item.split("=")[1])
        : [];

      const filters = filterValues.length ? filterValues.join(",") : "";

      const taxonomy = isActiveFilters ? `${fragments[0].split("=")[0]}=` : "&fs";
      const queryParams = {
        params: sectionData?.params,
        currentPage: 1,
        taxonomy,
        filters,
        b16: true,
      };
      setQueryString(queryParams);
    } else {
      setQueryString({taxonomy: "", filters: "", b16: true});
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setIsInitialPage(false);
    setQueryString({currentPage: pageNumber, b16: true, params: sectionData?.params});
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: 1
  useEffect(() => {
    if (!isInitialPage && sectionRef?.current && navigation.state !== "loading") {
      sectionRef.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
  }, [currentPage, navigation.state]);

  if (navigation.state === "loading") {
    return;
  }

  return (
    <div ref={sectionRef}>
      <SectionWrapper
        id={id}
        isFirstSection={isFirstSection}
        isLastSection={isLastSection}
        backgroundColor={bgColor}
        // bgImage={slider ? null : b16bg}
        styles={{paddingBlock: isMobile ? 30 : 40}}
      >
        {!hideTitle && (
          <TitleUnderlinedAnimated
            wrapperStyles={{
              marginBottom: isMobile ? 24 : 35,
              gridColumn: "1/-1",
            }}
          >
            {sectionData?.title}
          </TitleUnderlinedAnimated>
        )}

        {showFilters && moreThanOneFilter && !slider && (
          <Filters
            handleFilter={handleFilter}
            activeFilters={activeFilters}
            sectionData={sectionData}
          />
        )}
        {articleList && (
          <ResultsAmountWrapper>
            <Text type="titleExtraSmall">
              {amountArticles} {resultTranslation}
            </Text>
          </ResultsAmountWrapper>
        )}

        {isEmptyResponse ? (
          <div
            style={{
              gridColumn: isMobile ? "1/-1" : "7/11",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 40,
            }}
          >
            <Text>
              {noContentFound ||
                "Nothing matches your search.\n" + "Take a look at our suggestions."}
            </Text>
          </div>
        ) : slider ? (
          <SliderWrapper backgroundColor={bgColor} styles={{paddingTop: 0}}>
            <Slider
              key={isMobile ? 0 : 1}
              freeMode={!isMobile}
              spaceBetween={20}
              scrollbar={{draggable: !isMobile, dragSize: isMobile ? 144 : 474}}
              style={{
                overflow: "initial",
              }}
            >
              {!!localFiltersData?.length &&
                localFiltersData?.map((card, i) => {
                  return (
                    <Slide key={i}>
                      <SliderCardsWrapper>
                        <CrossContentCard
                          key={cardId(card)}
                          styles={
                            isMobile
                              ? {width: 336, height: 438}
                              : {width: 313, height: 460}
                          }
                          cardData={card}
                          country={country}
                          isDestinationPage={isDestinationPage}
                        />
                      </SliderCardsWrapper>
                    </Slide>
                  );
                })}
            </Slider>
          </SliderWrapper>
        ) : (
          <InnerWrapper>
            {!!localFiltersData?.length &&
              localFiltersData?.map((card, i) => {
                return (
                  <CardsWrapper key={i}>
                    <CrossContentCard
                      imageLayout="cardGrid"
                      styles={{
                        aspectRatio: isMobile ? "9/11" : "inherit",
                        height: isDesktop && !xl ? 460 : "auto",
                      }}
                      key={cardId(card)}
                      cardData={card}
                      country={country}
                      isDestinationPage={isDestinationPage}
                    />
                  </CardsWrapper>
                );
              })}
          </InnerWrapper>
        )}

        {totalPages > 1 && !slider && (
          <ArticlePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pagination={pagination}
          />
        )}

        {isHomePage && language === "fr" && cta?.url && cta?.title && (
          <CTA
            target={cta?.target}
            buttonStyles={{
              gridColumn: isMobile ? "1/-1" : "7/11",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginInline: "auto",
              marginTop: 20,
            }}
            size="medium"
            buttonColor={theme?.colors?.brandBlue}
            type="primary"
            to={cta.url}
          >
            {cta?.title}
          </CTA>
        )}
      </SectionWrapper>
    </div>
  );
};

export default B16Articles;
